/**
 * Transition Parameters and Validation Rules Type Definitions
 * 
 * These types define the structure for transition_parameters and validation_rules JSON fields.
 * 
 * KEY PRINCIPLE:
 * - Prime Transitions: Define parameter TYPES (value is null)
 * - Company Transitions: Fill in actual VALUES
 */

// ============================================================================
// Parameter Definitions
// ============================================================================

export interface ParameterDefinition<T = any> {
  type: 'number' | 'boolean' | 'string' | 'array' | 'object';
  unit?: string;
  time_period?: 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
  currency?: 'USD' | 'EUR' | 'GBP';
  per?: string;
  description?: string;
  default?: T;
  value: T | null;  // null in prime, actual value in company
}

// Demand Parameters
export interface DemandParameters {
  target_value?: ParameterDefinition<number>;
  capacity_value?: ParameterDefinition<number>;
}

// Conversion Parameters
export interface ConversionParameters {
  conversion_rate?: ParameterDefinition<number>;  // Output per input (e.g., 0.95 = 95% yield)
  material_quantity?: ParameterDefinition<number>;  // Materials consumed per execution (e.g., 2 wheels per bike)
  resource_quantity?: ParameterDefinition<number>;  // Resources required per execution
  batch_size?: ParameterDefinition<number>;  // Items processed in a batch
  scrap_rate?: ParameterDefinition<number>;  // Percentage scrapped (0-1)
}

// Timing Parameters
export interface TimingParameters {
  duration?: ParameterDefinition<number>;  // Time to complete one execution
  setup_time?: ParameterDefinition<number>;  // Setup time before processing
  lead_time?: ParameterDefinition<number>;  // Total time from start to completion
  cycle_time?: ParameterDefinition<number>;  // Time between completing successive items
}

// Cost Parameters
export interface CostParameters {
  material_cost?: ParameterDefinition<number>;
  labor_cost?: ParameterDefinition<number>;
  overhead_cost?: ParameterDefinition<number>;
}

// Quality Parameters
export interface QualityParameters {
  defect_rate?: ParameterDefinition<number>;  // Expected defect rate (0-1)
  rework_rate?: ParameterDefinition<number>;  // Percentage requiring rework (0-1)
  inspection_required?: ParameterDefinition<boolean>;
}

// Inventory Parameters
export interface InventoryParameters {
  min_buffer?: ParameterDefinition<number>;  // Minimum inventory buffer
  max_buffer?: ParameterDefinition<number>;  // Maximum inventory buffer
  reorder_point?: ParameterDefinition<number>;  // Reorder trigger point
  economic_order_quantity?: ParameterDefinition<number>;  // Optimal order size
}

// Rollup/Aggregation Parameters
export interface RollupParameters {
  // Data Source Configuration
  source_artifact_type?: ParameterDefinition<string>;  // e.g., "revenue", "expense"
  source_state_filter?: ParameterDefinition<string[]>; // States to include (null = any state)
  
  // Aggregation Configuration
  aggregation_enabled?: ParameterDefinition<boolean>;  // Enable rollup for this transition
  aggregation_type?: ParameterDefinition<'time_based' | 'parent_based'>;  // How to group items
  aggregation_period?: ParameterDefinition<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>;
  aggregation_timezone?: ParameterDefinition<string>;  // e.g., "America/Denver"
  
  // Parent-Based Aggregation (for Invoice → Line Items)
  parent_artifact_ref_field?: ParameterDefinition<string>;  // e.g., "invoice_id"
  parent_artifact_type?: ParameterDefinition<string>;       // e.g., "invoice"
  
  // Selector/Filter Configuration
  selector_field?: ParameterDefinition<string>;  // Field to match (e.g., "recognized_bucket_id")
  selector_comparison?: ParameterDefinition<'equals' | 'in' | 'between'>;
  selector_value?: ParameterDefinition<string>;  // Value or expression (e.g., "$.bucket_id")
  
  // Measure Definitions (what to calculate)
  measure_total_field?: ParameterDefinition<string>;  // Field to sum (e.g., "amount_recognized_conv")
  measure_total_operation?: ParameterDefinition<'sum' | 'avg' | 'min' | 'max' | 'count'>;
  measure_count_enabled?: ParameterDefinition<boolean>;  // Count records
  measure_conditional_field?: ParameterDefinition<string>;  // Conditional measure field
  measure_conditional_operation?: ParameterDefinition<'sum_if' | 'count_if'>;
  measure_conditional_filter?: ParameterDefinition<string>;  // Condition (e.g., "type == 'refund'")
  
  // Idempotency Configuration
  idempotency_enabled?: ParameterDefinition<boolean>;
  idempotency_event_id_field?: ParameterDefinition<string>;  // e.g., "recognition_event_id"
  idempotency_watermark_field?: ParameterDefinition<string>;  // e.g., "last_processed_event_seq"
  
  // Window Configuration
  window_start_field?: ParameterDefinition<string>;  // e.g., "bucket_start"
  window_end_field?: ParameterDefinition<string>;    // e.g., "bucket_end"
  window_include_boundaries?: ParameterDefinition<boolean>;
  
  // Target/Threshold Configuration
  target_field_source?: ParameterDefinition<string>;  // e.g., "config.target_recognized_monthly"
  target_comparison_enabled?: ParameterDefinition<boolean>;
  
  // Bucket Key Configuration
  bucket_key_format?: ParameterDefinition<string>;  // e.g., "YYYY-MM" for monthly
  bucket_key_field?: ParameterDefinition<string>;   // Field where bucket key is stored
  
  // Multi-State Input Configuration (for MATERIAL transitions that read without changing state)
  multi_state_input_enabled?: ParameterDefinition<boolean>;  // Allow reading items in any state
  preserve_source_state?: ParameterDefinition<boolean>;      // Don't change source item states
}

// Artifact Generation Parameters (for CREATION transitions)
export interface ArtifactGenerationParameters {
  // Generation Mode
  artifact_generation_mode?: ParameterDefinition<'singleton' | 'sequential' | 'concurrent'>;
  
  // Singleton Config
  replacement_behavior?: ParameterDefinition<'archive_previous' | 'delete_previous' | 'error_if_exists'>;
  
  // Sequential Config
  completion_required?: ParameterDefinition<boolean>;
  max_active_items?: ParameterDefinition<number>;
  
  // Concurrent Config
  max_total_items?: ParameterDefinition<number>;
}

// Custom Parameters (flexible)
export interface CustomParameters {
  [key: string]: ParameterDefinition;
}

// Property Changes (how properties change during transition)
export interface PropertyChange {
  property_key: string;
  change_type: 'set' | 'increment' | 'decrement' | 'copy_from' | 'calculate' | 'copy_from_artifact' | 'aggregate_from_artifacts' | 'date_range_check';
  target_value?: any;
  source_property?: string; // for copy_from (same artifact) or copy_from_artifact (other artifact)
  expression?: string; // for calculate or date_range_check (auto-generated)
  condition?: ValidationRule; // optional condition for when change applies
  
  // Cross-artifact property reference (for copy_from_artifact)
  source_artifact_id?: string; // Specific prime artifact ID to reference
  source_artifact_filter?: ArtifactItemFilter; // Optional filter to find specific artifact item (if source_artifact_id not provided)
  
  // Aggregation from artifacts (for aggregate_from_artifacts)
  aggregation_operation?: 'count' | 'sum' | 'avg' | 'min' | 'max'; // Aggregation operation
  aggregation_artifact_id?: string; // Specific prime artifact ID to aggregate from
  aggregation_filter?: ArtifactItemFilter; // Filter criteria for aggregation (if aggregation_artifact_id not provided)
  aggregation_property?: string; // Property to aggregate (for sum/avg/min/max)
  
  // Date range check (for date_range_check)
  date_range_start_property?: string; // Property containing start date
  date_range_end_property?: string; // Property containing end date (optional)
  date_range_value_if_true?: any; // Value to set if date is within range
  date_range_value_if_false?: any; // Value to set if date is outside range
}

// Filter for finding artifact items
export interface ArtifactItemFilter {
  artifact_id?: string; // Filter by specific prime artifact ID (preferred)
  artifact_type?: string; // Filter by artifact type name (fallback if artifact_id not provided)
  property_filters?: PropertyFilter[]; // Filter by property values
  state_filter?: string[]; // Filter by state names
}

// Property-based filter condition
export interface PropertyFilter {
  property_key: string;
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'greater_or_equal' | 'less_or_equal' | 'contains' | 'in';
  value: any; // Value to compare against
}

// Complete Transition Parameters
export interface TransitionParameters {
  demand_parameters?: DemandParameters;
  conversion_parameters?: ConversionParameters;
  timing_parameters?: TimingParameters;
  cost_parameters?: CostParameters;
  quality_parameters?: QualityParameters;
  inventory_parameters?: InventoryParameters;
  rollup_parameters?: RollupParameters;
  artifact_generation_parameters?: ArtifactGenerationParameters;
  custom_parameters?: CustomParameters;
  property_changes?: PropertyChange[]; // Property changes during transition
}

// ============================================================================
// Validation Rule Definitions
// ============================================================================

export type ValidationSeverity = 'error' | 'warning' | 'info';
export type ValidationOperator = '>' | '>=' | '<' | '<=' | '==' | '!=';

export interface BaseValidationRule {
  description?: string;
  severity: ValidationSeverity;
}

export interface NumberValidationRule extends BaseValidationRule {
  type: 'number';
  operator: ValidationOperator;
  value: number | null;
}

export interface BooleanValidationRule extends BaseValidationRule {
  type: 'boolean';
  value: boolean | null;
  exceptions?: string[];  // e.g., ['CREATION'] for from_state_required
  allowed_for?: string[];  // e.g., ['MATERIAL', 'RESOURCE'] for same_state_allowed
}

export interface RangeValidationRule extends BaseValidationRule {
  type: 'range';
  min: number | null;
  max: number | null;
}

export interface ComparisonValidationRule extends BaseValidationRule {
  type: 'comparison';
  field1: string;
  operator: ValidationOperator;
  field2: string;
  and?: ComparisonValidationRule;  // For chained comparisons
}

export interface CalculationValidationRule extends BaseValidationRule {
  type: 'calculation';
  formula: string;  // e.g., "scrap_rate + defect_rate"
  operator: ValidationOperator;
  value: number | null;
}

export interface TimeRangeValidationRule extends BaseValidationRule {
  type: 'time_range';
  start: string | null;  // e.g., "08:00"
  end: string | null;    // e.g., "17:00"
}

export interface ArrayValidationRule extends BaseValidationRule {
  type: 'array';
  values: string[] | null;  // e.g., ["Monday", "Tuesday", ...]
}

export interface PropertyValidationRule extends BaseValidationRule {
  type: 'property';
  property_key: string;
  operator: ValidationOperator;
  value: any;  // Comparison value (can be literal or another property)
  use_property_value?: boolean;  // If true, compare property to another property
  compare_to_property?: string;  // Property to compare against
  use_date_comparison?: boolean;  // If true, use date comparison (e.g., today between dates)
  date_comparison_type?: 'today_between' | 'date_between' | 'date_after' | 'date_before';  // Type of date comparison
  date_start_property?: string;  // Property for start date (for date_between)
  date_end_property?: string;  // Property for end date (for date_between)
  validate_format?: boolean;  // If true, validate input format
  format_type?: 'email' | 'url' | 'phone' | 'date' | 'datetime' | 'number' | 'integer' | 'regex';  // Format to validate
  format_pattern?: string;  // Regex pattern if format_type is 'regex'
}

// Complex condition with AND/OR logic
export interface ComplexValidationRule extends BaseValidationRule {
  type: 'complex';
  logic: 'AND' | 'OR';  // How to combine conditions
  conditions: ValidationRule[];  // Array of conditions to combine
}

// Improvement Artifact Validation Rule
export interface ImprovementArtifactValidationRule extends BaseValidationRule {
  type: 'improvement_artifact_check';
  check_type: 'most_recent' | 'any' | 'active_only';
  require_active: boolean;
  auto_create_if_missing: boolean;
  creation_transition_name?: string; // Name of transition to trigger if missing
  error_message?: string; // Custom error message if validation fails
}

// Item Generation Validation Rule
export interface ItemGenerationValidationRule extends BaseValidationRule {
  type: 'item_generation_check';
  generation_type: 'singleton' | 'reporting_period' | 'transactional';
  error_message?: string; // Custom error message if validation fails
}

export type ValidationRule =
  | NumberValidationRule
  | BooleanValidationRule
  | RangeValidationRule
  | ComparisonValidationRule
  | CalculationValidationRule
  | TimeRangeValidationRule
  | ArrayValidationRule
  | PropertyValidationRule
  | ComplexValidationRule
  | ImprovementArtifactValidationRule
  | ItemGenerationValidationRule;

// Demand Validation
export interface DemandValidation {
  min_demand?: NumberValidationRule;
  max_demand?: NumberValidationRule;
  demand_increment?: NumberValidationRule;
}

// Conversion Validation
export interface ConversionValidation {
  conversion_rate_range?: RangeValidationRule;
  material_quantity_positive?: NumberValidationRule;
  scrap_rate_range?: RangeValidationRule;
}

// Timing Validation
export interface TimingValidation {
  duration_positive?: NumberValidationRule;
  lead_time_minimum?: NumberValidationRule;
  cycle_time_vs_duration?: ComparisonValidationRule;
}

// Capacity Validation
export interface CapacityValidation {
  max_capacity?: NumberValidationRule;
  utilization_warning?: NumberValidationRule;
}

// Quality Validation
export interface QualityValidation {
  defect_rate_threshold?: NumberValidationRule;
  combined_loss_rate?: CalculationValidationRule;
}

// Inventory Validation
export interface InventoryValidation {
  buffer_relationship?: ComparisonValidationRule;
  reorder_point_range?: ComparisonValidationRule;
}

// State Transition Validation
export interface StateTransitionValidation {
  from_state_required?: BooleanValidationRule;
  to_state_required?: BooleanValidationRule;
  same_state_allowed?: BooleanValidationRule;
}

// Business Rules
export interface BusinessRules {
  minimum_order_value?: CalculationValidationRule;
  working_hours?: TimeRangeValidationRule;
  available_days?: ArrayValidationRule;
  [key: string]: ValidationRule | undefined;
}

// Property-Based Validation
export interface PropertyValidation {
  [key: string]: PropertyValidationRule; // Flexible property-based rules
}

// Custom Validation (flexible)
export interface CustomValidation {
  [key: string]: ValidationRule;
}

// Complete Validation Rules
export interface ValidationRules {
  demand_validation?: DemandValidation;
  conversion_validation?: ConversionValidation;
  timing_validation?: TimingValidation;
  capacity_validation?: CapacityValidation;
  quality_validation?: QualityValidation;
  inventory_validation?: InventoryValidation;
  state_transition_validation?: StateTransitionValidation;
  business_rules?: BusinessRules;
  property_validation?: PropertyValidation;
  custom_validation?: CustomValidation;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get parameter value with fallback logic:
 * 1. Check company transition for value
 * 2. If null, check prime transition for value
 * 3. If null, use default from parameter definition
 * 4. If no default, use provided fallback
 */
export function getParameterValue<T = any>(
  params: TransitionParameters | null | undefined,
  category: keyof TransitionParameters,
  paramName: string,
  fallback?: T
): T | null {
  if (!params || !params[category]) {
    return fallback ?? null;
  }

  const categoryParams = params[category] as Record<string, ParameterDefinition<T>>;
  const param = categoryParams[paramName];

  if (!param) {
    return fallback ?? null;
  }

  return param.value ?? param.default ?? fallback ?? null;
}

/**
 * Validate a parameter value against a validation rule
 */
export function validateParameter(
  value: any,
  rule: ValidationRule
): { valid: boolean; message?: string; severity: ValidationSeverity } {
  switch (rule.type) {
    case 'number': {
      const numRule = rule as NumberValidationRule;
      if (numRule.value === null) {
        return { valid: true, severity: numRule.severity };
      }

      let valid = true;
      switch (numRule.operator) {
        case '>':
          valid = value > numRule.value;
          break;
        case '>=':
          valid = value >= numRule.value;
          break;
        case '<':
          valid = value < numRule.value;
          break;
        case '<=':
          valid = value <= numRule.value;
          break;
        case '==':
          valid = value === numRule.value;
          break;
        case '!=':
          valid = value !== numRule.value;
          break;
      }

      return {
        valid,
        message: valid ? undefined : `Value must be ${numRule.operator} ${numRule.value}`,
        severity: numRule.severity,
      };
    }

    case 'boolean': {
      const boolRule = rule as BooleanValidationRule;
      return {
        valid: boolRule.value === null || value === boolRule.value,
        message: boolRule.description,
        severity: boolRule.severity,
      };
    }

    case 'range': {
      const rangeRule = rule as RangeValidationRule;
      const min = rangeRule.min ?? -Infinity;
      const max = rangeRule.max ?? Infinity;
      const valid = value >= min && value <= max;
      return {
        valid,
        message: valid ? undefined : `Value must be between ${min} and ${max}`,
        severity: rangeRule.severity,
      };
    }

    case 'comparison':
    case 'calculation':
    case 'time_range':
    case 'array':
      // These require more complex validation logic with context
      return {
        valid: true,
        message: `${rule.type} validation requires context`,
        severity: rule.severity,
      };

    default:
      return { valid: true, severity: 'info' };
  }
}

/**
 * Get all validation errors for a set of parameters
 */
export function validateTransitionParameters(
  params: TransitionParameters,
  validationRules: ValidationRules
): Array<{ field: string; message: string; severity: ValidationSeverity }> {
  const errors: Array<{ field: string; message: string; severity: ValidationSeverity }> = [];

  // Helper to validate a category
  const validateCategory = (
    category: keyof TransitionParameters,
    validationCategory: keyof ValidationRules
  ) => {
    const categoryParams = params[category];
    const categoryRules = validationRules[validationCategory];

    if (!categoryParams || !categoryRules) return;

    Object.entries(categoryRules).forEach(([ruleName, rule]) => {
      const paramName = ruleName.replace(/_validation$|_range$|_positive$|_threshold$/g, '');
      const paramValue = getParameterValue(params, category, paramName);

      if (paramValue !== null && rule) {
        const result = validateParameter(paramValue, rule as ValidationRule);
        if (!result.valid && result.message) {
          errors.push({
            field: `${category}.${paramName}`,
            message: result.message,
            severity: result.severity,
          });
        }
      }
    });
  };

  // Validate each category
  validateCategory('demand_parameters', 'demand_validation');
  validateCategory('conversion_parameters', 'conversion_validation');
  validateCategory('timing_parameters', 'timing_validation');
  validateCategory('quality_parameters', 'quality_validation');
  validateCategory('inventory_parameters', 'inventory_validation');

  return errors;
}

/**
 * Create a default parameter definition
 */
export function createParameterDefinition<T>(
  type: ParameterDefinition['type'],
  defaultValue?: T,
  options?: Partial<ParameterDefinition<T>>
): ParameterDefinition<T> {
  return {
    type,
    default: defaultValue,
    value: null,
    ...options,
  };
}

/**
 * Merge prime and company parameters (company overrides prime)
 */
export function mergeParameters(
  primeParams: TransitionParameters | null,
  companyParams: TransitionParameters | null
): TransitionParameters {
  if (!primeParams) return companyParams || {};
  if (!companyParams) return primeParams;

  const merged: TransitionParameters = {};

  // Merge each category
  const categories: Array<keyof TransitionParameters> = [
    'demand_parameters',
    'conversion_parameters',
    'timing_parameters',
    'cost_parameters',
    'quality_parameters',
    'inventory_parameters',
    'custom_parameters',
  ];

  categories.forEach((category) => {
    const primeCategory = primeParams[category];
    const companyCategory = companyParams[category];

    if (primeCategory || companyCategory) {
      merged[category] = {
        ...primeCategory,
        ...companyCategory,
      } as any;
    }
  });

  return merged;
}

